import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { YStack, Text, Spinner } from "tamagui";
import { AxiosResponse } from "axios";
import api, { handleRequest } from "@services/axios/apiManager";
import { colors } from "@styles/colors";
import { useCusAuth } from "@utils/store/auth/useCusAuth";
import { IdentityContext, useUser } from "@utils/store/user/useUser";
import { pickInviteContext } from "@utils/func/pickInviteContext";

/**
 * 瓦斯帳戶邀請入口（不帶 ID）。
 *
 * Rich menu 網址必須靜態，不可內嵌某個人的 contextId，因此另開這個入口：
 * 掛載後自己查出使用者的瓦斯帳戶，挑出可用來邀請的 context，
 * 再轉址到既有的邀請頁 `/account/detail/:contextId/invite`。
 *
 * 關鍵：既有邀請頁的 owner 判斷純靠 router state（沒有 API fallback），
 * 轉址時必須把完整的 IdentityContext 物件塞進 state.context，
 * 否則使用者按「產生邀請碼」會被前端擋下。
 */
const InviteEntry = () => {
  const navigate = useNavigate();
  const { cusJwtToken } = useCusAuth();
  const { chooseIdentity } = useUser();
  // 防止 effect 重複觸發造成重複導頁
  const navigatedRef = useRef(false);

  useEffect(() => {
    if (!cusJwtToken) return;
    if (navigatedRef.current) return;

    // 送出請求前就同步上鎖：StrictMode 下 effect 會 mount → unmount → remount 連跑兩次，
    // 若鎖延到 callback 內才設，兩次呼叫都會在鎖生效前通過 guard，重複打 API。
    navigatedRef.current = true;

    handleRequest({
      asyncRequest: () =>
        api.get("/app-api/customer/contexts", {
          headers: { Authorization: `Bearer ${cusJwtToken}` },
        }),
      successCallback: (res: AxiosResponse) => {
        const { data } = res.data;
        const contexts = (data?.contexts ?? []) as IdentityContext[];
        const picked = pickInviteContext(contexts, data?.default_context_id, chooseIdentity);

        if (picked) {
          navigate(`/account/detail/${picked.context_id}/invite`, {
            state: { context: picked },
            replace: true,
          });
        } else {
          navigate("/account", { replace: true });
        }
      },
      errorCallback: (error: unknown) => {
        console.error("fetch contexts err", error);
        if (navigatedRef.current) return;
        navigatedRef.current = true;
        navigate("/account", { replace: true });
      },
    });
  }, [cusJwtToken, chooseIdentity, navigate]);

  return (
    <YStack f={1} width="100vw" height="100dvh" ai="center" jc="center" bg={colors.bg} gap="$4">
      <Spinner size="large" color={colors.primary} />
      <Text fontSize={14} color={colors.gray}>
        正在為您準備邀請頁面…
      </Text>
    </YStack>
  );
};

export default InviteEntry;
